import { APP_INITIALIZER, ApplicationConfig, provideBrowserGlobalErrorListeners } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthUser } from './core/models/user.model';
import { AuthActions } from './core/state/auth/auth.actions';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { authInterceptor } from './core/interceptors/http.interceptor';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideApiConfiguration } from 'amoxcalli-identity-api';
import { environment } from '../environments/environment';

import { routes } from './app.routes';
import { authFeature } from './core/state/auth/auth.reducer';
import { AuthEffects } from './core/state/auth/auth.effects';
import { hijosFeature } from './core/state/hijos/hijos.reducer';
import { HijosEffects } from './core/state/hijos/hijos.effects';
import { calificacionesFeature } from './core/state/calificaciones/calificaciones.reducer';
import { CalificacionesEffects } from './core/state/calificaciones/calificaciones.effects';
import { asistenciaFeature } from './core/state/asistencia/asistencia.reducer';
import { AsistenciaEffects } from './core/state/asistencia/asistencia.effects';
import { historialMedicoFeature } from './core/state/historial-medico/historial-medico.reducer';
import { HistorialMedicoEffects } from './core/state/historial-medico/historial-medico.effects';
import { tareasFeature } from './core/state/tareas/tareas.reducer';
import { TareasEffects } from './core/state/tareas/tareas.effects';

const AmoxcalliPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50:  '#F1F3F9',
      100: '#E3E7F3',
      200: '#C7CFE6',
      300: '#A3AFD3',
      400: '#7885B4',
      500: '#405189',
      600: '#364572',
      700: '#2C3960',
      800: '#232D4C',
      900: '#1A2138',
      950: '#10152B',
    },
    colorScheme: {
      light: {
        surface: {
          0:   '#FFFFFF',
          50:  '#F8FAFC',
          100: '#F1F5F9',
          200: '#E2E8F0',
          300: '#CBD5E1',
          400: '#94A3B8',
          500: '#64748B',
          600: '#475569',
          700: '#334155',
          800: '#1E293B',
          900: '#0F172A',
          950: '#060D1B',
        },
      },
      dark: {
        surface: {
          0:   '#FFFFFF',
          50:  '#F8FAFC',
          100: '#F1F5F9',
          200: '#E2E8F0',
          300: '#CBD5E1',
          400: '#94A3B8',
          500: '#64748B',
          600: '#475569',
          700: '#334155',
          800: '#1E293B',
          900: '#0F172A',
          950: '#060D1B',
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    MessageService,
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),
    provideStore({
      [authFeature.name]: authFeature.reducer,
      [hijosFeature.name]: hijosFeature.reducer,
      [calificacionesFeature.name]: calificacionesFeature.reducer,
      [asistenciaFeature.name]: asistenciaFeature.reducer,
      [historialMedicoFeature.name]: historialMedicoFeature.reducer,
      [tareasFeature.name]: tareasFeature.reducer,
    }),
    provideEffects(AuthEffects, HijosEffects, CalificacionesEffects, AsistenciaEffects, HistorialMedicoEffects, TareasEffects),
    {
      provide: APP_INITIALIZER,
      useFactory: (store: Store) => () => {
        const token    = localStorage.getItem('amxp_token');
        const userJson = localStorage.getItem('amxp_user');
        if (token && userJson) {
          try {
            const user = JSON.parse(userJson) as AuthUser;
            store.dispatch(AuthActions.hydrate({ user, token }));
          } catch { /* ignore corrupt storage */ }
        }
      },
      deps: [Store],
      multi: true,
    },
    provideApiConfiguration(environment.serverUrl.identity),
    providePrimeNG({
      theme: {
        preset: AmoxcalliPreset,
        options: { darkModeSelector: '.dark' },
      },
      ripple: true,
      translation: {
        dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
        dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        monthNames: [
          'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
          'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
        ],
        monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
        today: 'Hoy',
        clear: 'Limpiar',
        weekHeader: 'Sem',
        firstDayOfWeek: 1,
        dateFormat: 'dd/mm/yy',
        weak: 'Débil',
        medium: 'Media',
        strong: 'Fuerte',
        passwordPrompt: 'Escribe una contraseña',
        emptyMessage: 'No hay resultados',
        emptyFilterMessage: 'No se encontraron resultados',
        accept: 'Sí',
        reject: 'No',
        choose: 'Elegir',
        upload: 'Subir',
        cancel: 'Cancelar',
      },
    }),
  ],
};
